import { colors, fonts, radii } from '@steelwake/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import type { GameStatus } from '../../game/Game.js';
import { PracticeSource, practiceKey } from '../../game/match/localDriver.js';
import { freshSeed, matchSearch, parseMatchParams } from '../../game/match/setup.js';
import { useGame, type GameMatch } from '../../game/useGame.js';
import { OnlineSource } from '../../net/netSession.js';
import { parseOnlineParams, ticketStorageKey, withoutTicket } from '../../net/params.js';
import { Hud } from '../../hud/Hud.js';
import { Results } from '../../hud/Results.js';
import { useHudStore } from '../../hud/store.js';
import { FrameOverlay } from '../../settings/FrameOverlay.js';
import { SettingsMenu } from '../../settings/SettingsMenu.js';
import { useSettingsStore } from '../../settings/store.js';
import { Loading } from './Loading.js';

/** Escape both toggles the menu and (in the browser) releases the pointer lock; debounce. */
const MENU_DEBOUNCE_MS = 250;

function describe(status: GameStatus): string {
  switch (status.phase) {
    case 'probing':
      return 'Probing graphics backend…';
    case 'initialising':
      return `Starting ${status.message === 'webgl2' ? 'WebGL 2' : 'WebGPU'} renderer…`;
    case 'connecting':
      return status.message ?? 'Connecting…';
    case 'loading':
      return 'Loading arena and mechs…';
    case 'staged':
      return 'Ready to deploy';
    case 'ready':
      return 'ready';
    case 'error':
      return `Error: ${status.message ?? 'unknown'}`;
  }
}

/** The match: a full-bleed canvas outside the site shell with the HUD, menu and results on top. */
export function MatchRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  // Parsed once per address; a missing seed is generated here and written back so a reload
  // reproduces the same match.
  const params = useMemo(() => parseMatchParams(location.search), [location.search]);
  // `?room=` names an online match; everything else is a practice setup (DECISIONS.md #85).
  const online = useMemo(
    () =>
      parseOnlineParams(
        location.search,
        typeof sessionStorage === 'undefined' ? null : sessionStorage,
      ),
    [location.search],
  );
  const match = useMemo<GameMatch>(
    () =>
      online
        ? { source: new OnlineSource(online), key: `online|${online.roomId}` }
        : { source: new PracticeSource(params), key: practiceKey(params) },
    [online, params],
  );
  useEffect(() => {
    if (online) {
      // A ticket that arrived in the address is kept for reloads and taken out of the URL.
      if (online.ticketFromUrl && online.ticket) {
        try {
          sessionStorage.setItem(ticketStorageKey(online.roomId), online.ticket);
        } catch {
          // Session storage may be unavailable; the ticket still works for this page load.
        }
        window.history.replaceState(
          null,
          '',
          `${location.pathname}${withoutTicket(location.search)}`,
        );
      }
      return;
    }
    const canonical = matchSearch(params, location.search);
    if (canonical !== location.search)
      window.history.replaceState(null, '', `${location.pathname}${canonical}`);
  }, [online, params, location.pathname, location.search]);

  const mount = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const openedAt = useRef(0);
  const showStats = useSettingsStore((s) => s.settings.showStats);
  const bindings = useSettingsStore((s) => s.settings.bindings);
  const update = useSettingsStore((s) => s.update);
  const result = useHudStore((s) => s.snapshot?.result ?? null);

  const open = () => {
    openedAt.current = performance.now();
    setMenuOpen(true);
  };
  const { gameRef, status } = useGame(mount, match, {
    onEscapeKey: () => {
      if (menuOpen) {
        if (performance.now() - openedAt.current > MENU_DEBOUNCE_MS) setMenuOpen(false);
      } else {
        open();
      }
    },
    onPointerLockLost: () => {
      if (!menuOpen) open();
    },
    onToggleStats: () => update({ showStats: !useSettingsStore.getState().settings.showStats }),
    onSetupChange: (p) => {
      window.history.replaceState(
        null,
        '',
        `${location.pathname}${matchSearch(p, location.search)}`,
      );
    },
  });

  useEffect(() => {
    gameRef.current?.setInputEnabled(!menuOpen && result === null);
  }, [menuOpen, result, gameRef]);

  /** Frames stop before the route changes so the transition can commit at once. */
  const leaveTo = (to: string) => () => {
    gameRef.current?.stop();
    void navigate(to);
  };

  // An aborted online match (the opponent never showed) returns to the queue on its own.
  const onlinePhase = useHudStore((s) => s.snapshot?.online?.phase ?? null);
  useEffect(() => {
    if (!online || onlinePhase !== 'aborted') return;
    const timer = setTimeout(() => {
      gameRef.current?.stop();
      void navigate('/play/online');
    }, 3000);
    return () => clearTimeout(timer);
  }, [online, onlinePhase, gameRef, navigate]);

  const ready = status.phase === 'ready';
  const backend = gameRef.current?.backend ?? null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        background: colors.background,
        fontFamily: fonts.ui,
      }}
    >
      <div ref={mount} data-testid="arena-mount" style={{ position: 'absolute', inset: 0 }} />

      <div
        data-testid="play-status"
        data-phase={status.phase}
        role="status"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '10px 16px',
          background: 'rgba(11, 13, 16, 0.8)',
          border: `1px solid ${status.phase === 'error' ? colors.danger : colors.border}`,
          borderRadius: radii.md,
          color: status.phase === 'error' ? colors.danger : colors.text,
          fontSize: 14,
          opacity: ready ? 0 : 1,
          transition: 'opacity 300ms',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        {describe(status)}
      </div>

      {ready ? (
        <Hud showHint={!menuOpen && result === null} />
      ) : (
        <Loading
          status={status}
          descriptor={gameRef.current?.matchDescriptor ?? null}
          bindings={bindings}
          online={online !== null}
          onDeploy={() => gameRef.current?.deploy()}
        />
      )}

      <Link
        to="/play"
        data-testid="leave-arena"
        onClick={() => gameRef.current?.stop()}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          padding: '6px 10px',
          background: 'rgba(11, 13, 16, 0.6)',
          borderRadius: radii.sm,
          color: colors.textMuted,
          fontSize: 12,
          textDecoration: 'none',
          zIndex: 6,
        }}
      >
        Leave arena
      </Link>

      {showStats ? <FrameOverlay gameRef={gameRef} /> : null}
      {result !== null && !menuOpen ? (
        <Results
          onRematch={online ? undefined : () => gameRef.current?.rematch(freshSeed())}
          onQueue={online ? leaveTo('/play/online') : undefined}
          onChangeMech={
            online
              ? undefined
              : leaveTo(
                  `/play${location.search ? location.search.replace(/[?&](mech|opponent|bot|seed|roundSeconds|countdown|rounds|ult)=[^&]*/g, '').replace(/^&/, '?') : ''}`,
                )
          }
        />
      ) : null}
      {menuOpen ? <SettingsMenu backend={backend} onResume={() => setMenuOpen(false)} /> : null}
    </div>
  );
}
